<!--
File: TreatmentMatrixUnpavedPivot.vue
Description: shows the Treatment Matrix for Unpaved Roads as the pivot table.
-->
<template>
  <base-data-table v-if="eligible" :loading="showSpinner" :showPagination="false" @click="checkForRefresh">

    <template #header-buttons>
      <md-button v-if="isBtnAllowed('PrintButton')" class="md-success" @click="print" :disabled="showSpinner">
        {{ $t('buttons.print') }}
      </md-button>
      <!--md-button v-if="isBtnAllowed('ExportButton')" class="md-raised md-success" @click="exportToExcel"
        :disabled="showSpinner">
        {{ $t('buttons.excel_export') }}
      </md-button-->
    </template>

    <template #table-rows>
      <span id="matrix-unpaved" v-html="tableData" @focus="checkForRefresh"></span>
    </template>
  </base-data-table>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { BaseDataTable, TreatmentsDropdown } from '@/pages/Components/'
import BaseTableMixin from '@/mixins/BaseTableMixin'
import printReport from '@/mixins/printMixin'

export default {
  name: 'TreatmentMatrixUnpavedPivot',
  mixins: [printReport, BaseTableMixin],

  data() {
    return {
      formName: 'TreatmentMatrixPivot', // Use paved pivot permissions
      eligible: false,

      historyMapping: {
        pagination: {},
        searchQuery: ''
      }
    }
  },

  components: {
    BaseDataTable,
    TreatmentsDropdown,
  },

  async mounted() {
    // Ensure treatment matrix unpaved list is loaded for color mapping (this contains the color data)
    await this.ensureTreatmentMatrixUnpavedListLoaded();
  },

  activated() {
    // This lifecycle hook is called when the component becomes active (useful for keep-alive components)
    // Check if we need to refresh data
    this.checkForRefresh();
  },

  // Watch for when the component becomes visible (alternative to activated for non-keep-alive)
  watch: {
    '$el'() {
      // Component is mounted/remounted, check for refresh
      this.$nextTick(() => {
        this.checkForRefresh();
      });
    }
  },

  methods: {
    ...mapActions({
      clearList: 'ReferenceData/CLEAR_TREATMENT_MATRIX_UNPAVED_LIST',  // Called from the BaseTableMixin's mount()
      loadList: 'ReferenceData/LOAD_TREATMENT_MATRIX_UNPAVED_PIVOT_HTML',
      loadTreatmentMatrixUnpavedList: 'ReferenceData/LOAD_TREATMENT_MATRIX_UNPAVED_LIST'
    }),

    async ensureTreatmentMatrixUnpavedListLoaded() {
      // Load unpaved treatment matrix list for color mapping if not already loaded (like paved matrix)
      if (!this.treatmentMatrixUnpavedList || this.treatmentMatrixUnpavedList.length === 0) {
        try {
          await this.loadTreatmentMatrixUnpavedList({ forDropdown: false });
        } catch (error) {
          console.warn('Failed to load unpaved treatment matrix list for color mapping:', error);
        }
      }
    },

    print() {
      this.showSpinner = true
      // Wait a bit to ensure DOM is updated with colors
      this.$nextTick(() => {
        this.printReport(this.getPrintHeader)
        this.showSpinner = false
      })
    },

    async exportToExcel() {
      this.$store.dispatch('ReferenceData/LOAD_TREATMENT_MATRIX_UNPAVED_PIVOT_EXCEL').then(res => {
        const link = document.createElement('a')
        link.href = URL.createObjectURL(new Blob([res]))
        link.download = "treatment_matrix_unpaved.xlsx"
        link.click()
        URL.revokeObjectURL(link.href)
      })
    },

    async ensureTreatmentMatrixUnpavedListLoaded() {
      // Load treatment matrix unpaved list for color mapping if not already loaded
      if (!this.treatmentMatrixUnpavedList || this.treatmentMatrixUnpavedList.length === 0) {
        try {
          await this.loadTreatmentMatrixUnpavedList({ forDropdown: false });
        } catch (error) {
          console.warn('Failed to load unpaved treatment matrix list for color mapping:', error);
        }
      }
      // Also ensure the pivot HTML is loaded
      if (!this.pivot) {
        try {
          await this.loadList();
        } catch (error) {
          console.warn('Failed to load pivot HTML:', error);
        }
      }
    },

    applyTreatmentColors(html) {
      // Ensure html is a string
      if (typeof html !== 'string') {
        console.warn('applyTreatmentColors received non-string input:', html);
        return String(html || '');
      }

      // Use treatment matrix unpaved list which already contains color information from the API
      if (this.treatmentMatrixUnpavedList && Array.isArray(this.treatmentMatrixUnpavedList)) {
        const treatmentColorMap = {};
        
        // Build color map from treatment matrix unpaved API data 
        this.treatmentMatrixUnpavedList.forEach(treatment => {
          if (treatment.key && treatment.color) {
            treatmentColorMap[treatment.key] = treatment.color;
          }
        });

        // Apply colors based on database color configuration
        Object.keys(treatmentColorMap).forEach(treatmentCode => {
          const dbColor = treatmentColorMap[treatmentCode];
          
          // Convert database color names to hex values (comprehensive list for future API colors)
          const colorMap = {
            // Primary colors
            'red': '#F44336',
            'pink': '#E91E63',
            'purple': '#9C27B0',
            'violet': '#9C27B0',
            'indigo': '#3F51B5',
            'blue': '#2196F3',
            'cyan': '#00BCD4',
            'teal': '#009688',
            'green': '#4CAF50',
            'lime': '#CDDC39',
            'yellow': '#FFD700',
            'amber': '#FFC107',
            'orange': '#FF9800',
            'brown': '#795548',
            
            // Grayscale
            'black': '#000000',
            'grey': '#9E9E9E',
            'gray': '#9E9E9E',
            'white': '#FFFFFF',
            
            // Light variants
            'lightred': '#FFCDD2',
            'lightpink': '#F8BBD9',
            'lightpurple': '#E1BEE7',
            'lightblue': '#BBDEFB',
            'lightcyan': '#B2EBF2',
            'lightgreen': '#C8E6C9',
            'lightyellow': '#FFF9C4',
            'lightorange': '#FFE0B2',
            'lightgray': '#F5F5F5',
            'lightgrey': '#F5F5F5',
            
            // Dark variants
            'darkred': '#C62828',
            'darkpink': '#AD1457',
            'darkpurple': '#6A1B9A',
            'darkblue': '#1565C0',
            'darkcyan': '#00838F',
            'darkgreen': '#2E7D32',
            'darkyellow': '#F57F17',
            'darkorange': '#E65100',
            'darkgray': '#424242',
            'darkgrey': '#424242',
            
            // Additional common colors
            'maroon': '#800000',
            'navy': '#000080',
            'olive': '#808000',
            'aqua': '#00FFFF',
            'fuchsia': '#FF00FF',
            'silver': '#C0C0C0',
            'gold': '#FFD700',
            'coral': '#FF7F50',
            'salmon': '#FA8072',
            'crimson': '#DC143C',
            'magenta': '#FF00FF',
            'turquoise': '#40E0D0',
            'khaki': '#F0E68C',
            'plum': '#DDA0DD',
            'orchid': '#DA70D6'
          };
          
          // Use database color or fallback to the color value if it's already a hex
          const bgColor = colorMap[dbColor?.toLowerCase()] || dbColor || '#757575';
          
          // Determine text color based on background brightness (light colors get black text)
          const lightColors = ['yellow', 'lime', 'lightyellow', 'lightgreen', 'lightblue', 'lightcyan', 
                              'lightpink', 'lightpurple', 'lightorange', 'lightred', 'lightgray', 'lightgrey', 
                              'white', 'silver', 'khaki', 'amber'];
          const textColor = lightColors.includes(dbColor?.toLowerCase()) ? 'black' : 'white';
          
          // Try multiple regex patterns to catch all variations
          const patterns = [
            `<td([^>]*)>\\s*${treatmentCode}\\s*</td>`,
            `<td([^>]*)>${treatmentCode}</td>`,
            `>[\\s]*${treatmentCode}[\\s]*<`
          ];
          
          patterns.forEach(pattern => {
            const regex = new RegExp(pattern, 'gi');
            html = html.replace(regex, (match, attributes) => {
              if (match.includes('<td')) {
                // Full td replacement
                return `<td${attributes || ''} style="background-color: ${bgColor} !important; color: ${textColor} !important; font-weight: bold !important; text-align: center !important; border: 1px solid #ddd !important; padding: 8px !important;">${treatmentCode}</td>`;
              } else {
                // Just the content replacement
                return `>${treatmentCode}<`;
              }
            });
          });
        });
        
      } else {
        console.warn('Treatment matrix unpaved list not available for color mapping');
      }

      return html;
    },

    async refreshData() {
      // Reload both pivot HTML and list data for color mapping
      this.showSpinner = true;
      try {
        // Clear existing data
        await this.clearList();
        
        // Reload pivot HTML and list data
        await Promise.all([
          this.loadList(),
          this.loadTreatmentMatrixUnpavedList({ forDropdown: false })
        ]);
        
      } catch (error) {
        console.error('Error refreshing matrix data:', error);
      } finally {
        this.showSpinner = false;
      }
    },

    checkForRefresh() {
      const shouldRefresh = localStorage.getItem('unpaved_matrix_needs_refresh');
      if (shouldRefresh === 'true') {
        this.refreshData();
        localStorage.removeItem('unpaved_matrix_needs_refresh');
      }
    },
  },

  computed: {
    ...mapState({
      pivot: (state) => state.ReferenceData.treatmentMatrixUnpavedPivot,
      treatmentMatrixUnpavedList: (state) => state.ReferenceData.treatment_matrix_unpaved_list,
    }),
    ...mapGetters('ReferenceData', ['treatmentMatrixUnpavedEditable']),

    tableData() {
      // Check for refresh flag whenever this computed property is accessed
      // This ensures we catch tab switches when Vue re-renders the component
      const shouldRefresh = localStorage.getItem('unpaved_matrix_needs_refresh');
      if (shouldRefresh === 'true') {
        // Use nextTick to avoid infinite loops
        this.$nextTick(() => {
          this.refreshData();
          localStorage.removeItem('unpaved_matrix_needs_refresh');
        });
      }

      if (!this.pivot) return ''

      // Ensure pivot is a string
      let html = String(this.pivot || '');


      // Handle the pandas multi-level header structure
      // Remove extra header rows and create clean single header DYNAMICALLY
      html = html.replace(/<thead>[\s\S]*?<\/thead>/gi, function(match) {
        // Extract IRI ranges from the original header to build dynamic columns
        const iriMatches = match.match(/<th>(\d+\.\d+\s*-\s*\d+\.\d+)<\/th>/g);
        
        let headerCols = '<th style="background-color: #f0f0f0; font-weight: bold; text-align: center; padding: 5px; width: 140px; border: 1px solid black;">Traffic</th>';
        
        if (iriMatches && iriMatches.length > 0) {
          // Dynamically generate IRI column headers based on API response
          iriMatches.forEach(iriMatch => {
            const iriRange = iriMatch.match(/>(\d+\.\d+)\s*-\s*(\d+\.\d+)</);
            if (iriRange) {
              const startIri = parseFloat(iriRange[1]);
              const endIri = parseFloat(iriRange[2]);
              
              // Format as "IRI X-Y" (remove decimals if they're .0)
              const startLabel = startIri % 1 === 0 ? Math.floor(startIri) : startIri;
              const endLabel = endIri % 1 === 0 ? Math.floor(endIri) : endIri;
              
              // If start and end are the same, show "IRI X" instead of "IRI X-X"
              const iriLabel = startLabel === endLabel ? `IRI ${startLabel}` : `IRI ${startLabel}-${endLabel}`;
              
              headerCols += `<th style="background-color: #f0f0f0; font-weight: bold; text-align: center; padding: 5px; width: 120px; border: 1px solid black;">${iriLabel}</th>`;
            }
          });
        } else {
          // Fallback: if no IRI matches found, try to count columns and generate generic headers
          console.warn('No IRI ranges detected, using fallback column generation');
          const columnCount = (match.match(/<th[^>]*colspan="(\d+)"/)?.[1]) || 3;
          for (let i = 0; i < columnCount; i++) {
            headerCols += `<th style="background-color: #f0f0f0; font-weight: bold; text-align: center; padding: 5px; width: 120px; border: 1px solid black;">IRI Col ${i+1}</th>`;
          }
        }
        
        
        return `<thead>
          <tr>
            ${headerCols}
          </tr>
        </thead>`;
      });

      // Handle traffic (AADT) row header replacements with 90-degree rotation and improved readability
      // Split text into more lines for reduced row height
      const standardTrafficReplacements = {
        '0.0 - 200.0': '<div style="writing-mode: vertical-rl; text-orientation: mixed; transform: rotate(180deg); height: 80px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold; overflow: visible; white-space: normal; line-height: 1.2; text-align: center;">Low<br>traffic<br>&lt; 200<br>veh/day</div>',
        '200.0 - 500.0': '<div style="writing-mode: vertical-rl; text-orientation: mixed; transform: rotate(180deg); height: 80px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold; overflow: visible; white-space: normal; line-height: 1.2; text-align: center;">Medium<br>traffic<br>200-500<br>veh/day</div>',
        '500.0 - 1000.0': '<div style="writing-mode: vertical-rl; text-orientation: mixed; transform: rotate(180deg); height: 80px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold; overflow: visible; white-space: normal; line-height: 1.2; text-align: center;">High<br>traffic<br>500-1,000<br>veh/day</div>',
        '1000.0 - 999999.0': '<div style="writing-mode: vertical-rl; text-orientation: mixed; transform: rotate(180deg); height: 80px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold; overflow: visible; white-space: normal; line-height: 1.2; text-align: center;">Very high<br>traffic<br>&gt; 1,000<br>veh/day</div>',
      };

      // Apply standard traffic row header replacements
      Object.keys(standardTrafficReplacements).forEach(key => {
        const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        html = html.replace(regex, standardTrafficReplacements[key]);
      });

      // Handle any other custom traffic ranges dynamically
      // Look for any remaining numeric ranges that weren't replaced by standard categories
      // Apply to both table cells (td) and table headers (th) in the first column, but not header row
      html = html.replace(/<(td|th)([^>]*)>([^<]*)<\/(td|th)>/g, (match, tagName1, attrs, cellContent, tagName2) => {
        // Skip if this cell already has styling (i.e., it's already been processed)
        if (cellContent.includes('<div') || cellContent.includes('background-color') || cellContent.trim() === 'None' || /^[A-Z]+\d*$/.test(cellContent.trim())) {
          return match; // Return unchanged
        }
        
        // Skip if this appears to be a header row (contains "IRI" or has header-like styling)
        if (cellContent.includes('IRI') || attrs.includes('background-color: #f0f0f0')) {
          return match; // Return unchanged - this is a header
        }
        
        // Look for numeric ranges in the cell content
        const rangeMatch = cellContent.match(/(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
        if (!rangeMatch) {
          return match; // No range found, return unchanged
        }
        
        // Convert to numbers and format nicely
        const startNum = parseFloat(rangeMatch[1]);
        const endNum = parseFloat(rangeMatch[2]);
        
        // Format numbers (remove .0 if it's a whole number)
        const startLabel = startNum % 1 === 0 ? Math.floor(startNum) : startNum;
        const endLabel = endNum % 1 === 0 ? Math.floor(endNum) : endNum;
        
        // If start and end are the same, show single value instead of range
        const rangeLabel = startLabel === endLabel ? `${startLabel}` : `${startLabel} - ${endLabel}`;
        
        // Create "Other traffic" label for custom ranges with proper width
        return `<${tagName1}${attrs} style="width: 160px;"><div style="writing-mode: vertical-rl; text-orientation: mixed; transform: rotate(180deg); height: 80px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold; overflow: visible; white-space: normal; line-height: 1.2; text-align: center;">Other<br>traffic<br>${rangeLabel}<br>veh/day</div></${tagName2}>`;
      });

      // LOGICAL SORTING: Reorder rows in logical traffic order (Low → Medium → High → Very High)
      // Extract table content and reorder tbody rows
      const tbodyMatch = html.match(/<tbody>([\s\S]*?)<\/tbody>/);
      if (tbodyMatch) {
        const tbodyContent = tbodyMatch[1];
        
        // Extract individual rows
        const rowMatches = tbodyContent.match(/<tr>[\s\S]*?<\/tr>/g);
        if (rowMatches && rowMatches.length > 0) {
          
          // Define logical order mapping - Handle ALL traffic ranges including edge cases
          const logicalOrder = [
            'Low', // Low traffic < 200 
            'Medium', // Medium traffic 200-500
            'High', // High traffic 500-1,000  
            'Very high' // Very high traffic > 1,000
          ];
          
          // Sort rows based on logical traffic order, keeping unmatched rows at the end
          const sortedRows = [];
          const unmatchedRows = [];
          
          // First, add rows that match our logical order
          logicalOrder.forEach(trafficType => {
            const matchingRow = rowMatches.find(row => row.includes(trafficType));
            if (matchingRow) {
              sortedRows.push(matchingRow);
            }
          });
          
          // Then, add any rows that don't match our keywords (like 501.0 - 1001.0 with PAT)
          rowMatches.forEach(row => {
            const hasKeyword = logicalOrder.some(keyword => row.includes(keyword));
            if (!hasKeyword) {
              unmatchedRows.push(row);
            }
          });
          
          // Combine sorted rows + unmatched rows
          const allRows = [...sortedRows, ...unmatchedRows];
          
          // Rebuild table with all rows preserved
          const sortedTbody = `<tbody>\n    ${allRows.join('\n    ')}\n  </tbody>`;
          html = html.replace(/<tbody>[\s\S]*?<\/tbody>/, sortedTbody);
          
        }
      }

      // 100% API-DRIVEN: Apply colors using only database/API color configuration
      
      // Apply base styling to data cells first
      html = html.replace(/<td(?![^>]*style)/g, '<td style="text-align: center; padding: 8px; min-width: 96px; border: 1px solid black;"');

      // Use treatment matrix list which contains ALL color information from the API
      if (this.treatmentMatrixUnpavedList && Array.isArray(this.treatmentMatrixUnpavedList)) {
        const treatmentColorMap = {};
        
        // Build color map from unpaved treatment matrix API data 
        this.treatmentMatrixUnpavedList.forEach(treatment => {
          if (treatment.key && treatment.color) {
            treatmentColorMap[treatment.key] = treatment.color;
          }
        });

        
        // Check if PAT exists in the HTML before applying colors

        // Apply colors based ONLY on database color configuration
        Object.keys(treatmentColorMap).forEach(treatmentCode => {
          const dbColor = treatmentColorMap[treatmentCode];
          
          
          // Convert database color names to hex values (API-driven)
          const colorMap = {
            'green': '#4CAF50',
            'yellow': '#FFD700', 
            'red': '#F44336',
            'blue': '#2196F3',
            'orange': '#FF9800'
          };
          
          // Use database color or fallback to the color value if it's already a hex
          const bgColor = colorMap[dbColor?.toLowerCase()] || dbColor || '#757575';
          const textColor = (dbColor?.toLowerCase() === 'yellow') ? 'black' : 'white';
          
          // Create regex to match the treatment code in table cells
          // Escape special regex characters and handle Cyrillic characters properly
          const escapedCode = treatmentCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`<td([^>]*style="[^"]*")>\\s*${escapedCode}\\s*</td>`, 'gi');
          
          const beforeReplace = html.includes(treatmentCode);
          
          html = html.replace(regex, (match, styleAttr) => {
            const baseStyle = styleAttr.replace('style="', '').replace('"', '');
            return `<td style="${baseStyle} background-color: ${bgColor}; color: ${textColor}; font-weight: bold;">${treatmentCode}</td>`;
          });
          
          const afterReplace = html.includes(`background-color: ${bgColor}`);
        });
        
      } else {
        console.error('CRITICAL: Unpaved treatment matrix list not available - all colors should come from API!');
      }


      return html;
    },

    getPrintHeader() {
      const today = new Date().toLocaleDateString()
      const matrixElement = this.$el.querySelector('#matrix-unpaved');
      
      if (!matrixElement) {
        return `<h4 style='text-align:center'>${this.$t('reports.reports_title')}</h4>
                <h4 style='text-align:center'>${this.$t('route.treatment_matrix_unpaved')} ${this.$t('label.as_on')}${today}</h4>`
      }
      
      // Get the innerHTML and remove buttons, but preserve all styling including inline styles
      let prtHtml = matrixElement.innerHTML.replace(/<button[^>]*>.*?<\/button>/g, '');
      
      // Add print-specific styling to preserve colors
      const printStyles = `
        <style>
          @media print {
            * { -webkit-print-color-adjust: exact !important; }
            td[style*="background-color"] { -webkit-print-color-adjust: exact !important; }
            table { border-collapse: collapse !important; width: 100% !important; }
            td, th { border: 1px solid black !important; padding: 4px !important; }
          }
        </style>
      `;

      return `<h4 style='text-align:center'>${this.$t('reports.reports_title')}</h4>
              <h4 style='text-align:center'>${this.$t('route.treatment_matrix_unpaved')} ${this.$t('label.as_on')}${today}</h4>
              ${printStyles}
              ${prtHtml}`
    },
  },
}
</script>
<style lang="scss">
$table-cell-padding: .5rem; // default in bs5
$table-cell-padding-sm: .25rem; // default in bs5

.md-card {
  margin: 0px 0;
}

.text_rotate {
  rotate: -90deg;
  white-space: pre;
  width: 50px;
  text-align: center;
  padding: 0px;
}

/* Treatment Matrix Unpaved Color Coding - Colors applied directly via JavaScript */
/* Ensure proper spacing in table cells with reduced height */
#matrix-unpaved td {
  text-align: center !important;
  padding: 6px !important;
  height: 60px !important; /* Reduced from default height */
  vertical-align: middle !important;
}

#matrix-unpaved th {
  height: 60px !important; /* Reduced row header height */
  vertical-align: middle !important;
  padding: 4px !important;
}

#matrix-unpaved table {
  border: 1px solid black;
  border-collapse: collapse;
}

#matrix-unpaved td,
#matrix-unpaved th {
  border: 1px solid black;
}

/* Print styles for treatment matrix unpaved */
@media print {
  /* Ensure colors are preserved in print */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  /* Force cell colors to print */
  #matrix-unpaved td[style*="background-color"] {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  /* Ensure proper text visibility in print */
  #matrix-unpaved td {
    color: black !important;
  }
  
  /* Override text color for colored cells to maintain readability */
  #matrix-unpaved td[style*="background-color"][style*="color: white"] {
    color: white !important;
  }
  
  #matrix-unpaved td[style*="background-color"][style*="color: black"] {
    color: black !important;
  }
  
  /* Ensure table structure is preserved */
  #matrix-unpaved table {
    border-collapse: collapse !important;
    width: 100% !important;
  }
  
  #matrix-unpaved td,
  #matrix-unpaved th {
    border: 1px solid black !important;
    padding: 4px !important;
    font-size: 12px !important;
  }
  
  /* Fix any text that might not be black */
  #matrix-unpaved th {
    color: black !important;
    font-weight: bold !important;
  }
}
</style>
